import axios from 'axios';
import Vue from 'vue';
import { AutoModeNextItemResponse } from '@/models/AutoModeTypes';

const AUTO_MODE_SPECIFIC_QUEUE_KEY = 'autoModeSpecificQueue';

class AutoModeService {
    /**
     * Get the list of queues the current user has permission to access
     * @returns Promise with array of queue names
     */
    static async getAvailableQueues(): Promise<string[]> {
        try {
            const response = await axios.get<string[]>('/api/AutoMode/available-queues');
            return response.data;
        } catch (error) {
            console.error('Error getting available queues:', error);
            throw error;
        }
    }

    /**
     * Get the next available queue item for the current user
     * @param currentQueueType - Optional queue type of the current item (e.g., 'ConsultationQueue', 'TransferQueue')
     * @param currentItemId - Optional ID of the current item to find the next item after
     * @param specificQueue - Optional specific queue to filter to (if null/undefined, searches all queues)
     * @returns Promise with the next item response
     */
    static async getNextItem(currentQueueType?: string, currentItemId?: string | number, specificQueue?: string | null): Promise<AutoModeNextItemResponse> {
        try {
            const params = new URLSearchParams();
            if (currentQueueType) {
                params.append('currentQueueType', currentQueueType);
            }
            if (currentItemId !== undefined && currentItemId !== null) {
                params.append('currentItemId', String(currentItemId));
            }
            if (specificQueue) {
                params.append('specificQueue', specificQueue);
            }

            const url = params.toString() ? `/api/AutoMode/next?${params.toString()}` : '/api/AutoMode/next';
            const response = await axios.get<AutoModeNextItemResponse>(url);

            Vue.prototype.$store.lastAutoModeResponse = response.data;

            return response.data;
        } catch (error) {
            console.error('Error getting next auto mode item:', error);
            throw error;
        }
    }

    /**
     * Get the saved specific queue preference from localStorage
     * @returns The saved queue name, null if "All Queues" is selected, or null if not set
     */
    static getSavedSpecificQueue(): string | null {
        try {
            return localStorage.getItem(AUTO_MODE_SPECIFIC_QUEUE_KEY);
        } catch (error) {
            console.error('Error reading from localStorage:', error);
            return null;
        }
    }

    /**
     * Save the specific queue preference to localStorage
     * @param queueName - The queue name to save, or null for "All Queues"
     */
    static saveSpecificQueue(queueName: string | null): void {
        try {
            if (queueName === null) {
                localStorage.removeItem(AUTO_MODE_SPECIFIC_QUEUE_KEY);
            } else {
                localStorage.setItem(AUTO_MODE_SPECIFIC_QUEUE_KEY, queueName);
            }
        } catch (error) {
            console.error('Error writing to localStorage:', error);
            // Silently fail - localStorage quota exceeded or disabled in privacy mode
        }
    }

    /**
     * Clear the saved specific queue preference
     */
    static clearSpecificQueue(): void {
        try {
            localStorage.removeItem(AUTO_MODE_SPECIFIC_QUEUE_KEY);
        } catch (error) {
            console.error('Error clearing localStorage:', error);
            // Silently fail - localStorage disabled in privacy mode
        }
    }
}

export default AutoModeService;
